import { useState, useEffect } from 'react';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookie-consent', JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  const handleAcceptNecessary = () => {
    localStorage.setItem('cookie-consent', JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div 
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 animate-slide-up"
    >
      <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-[#00BCD4]/10 flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-[#00BCD4]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#0A192F] mb-1">
                Respectăm confidențialitatea dumneavoastră
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Folosim cookie-uri pentru a vă oferi cea mai bună experiență pe site-ul nostru. 
                Cookie-urile ne ajută să înțelegem cum folosiți site-ul și să îl îmbunătățim continuu.
              </p>
            </div>
          </div>

          {/* Details (expandable) */}
          {showDetails && (
            <div className="mb-6 p-4 bg-gray-50 rounded-xl space-y-3 animate-fade-in">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-[#0A192F]">Cookie-uri necesare</p>
                  <p className="text-xs text-gray-500">Esențiale pentru funcționarea site-ului</p>
                </div>
                <span className="text-xs text-[#00BCD4] font-medium bg-[#00BCD4]/10 px-2 py-1 rounded">Întotdeauna active</span>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-[#0A192F]">Cookie-uri analitice</p>
                  <p className="text-xs text-gray-500">Ne ajută să înțelegem cum este folosit site-ul</p>
                </div>
                <span className="text-xs text-gray-500">Opționale</span>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-[#0A192F]">Cookie-uri de marketing</p>
                  <p className="text-xs text-gray-500">Folosite pentru reclame personalizate</p>
                </div>
                <span className="text-xs text-gray-500">Opționale</span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <button
              onClick={handleAcceptAll}
              data-testid="cookie-accept-all"
              className="w-full sm:w-auto px-6 py-3 bg-[#00BCD4] hover:bg-[#00d4ef] text-[#0A192F] font-semibold rounded-xl transition-all duration-200 transform hover:scale-[1.02]"
            >
              Accept toate
            </button>
            <button
              onClick={handleAcceptNecessary}
              data-testid="cookie-accept-necessary"
              className="w-full sm:w-auto px-6 py-3 bg-[#0A192F] hover:bg-[#112240] text-white font-semibold rounded-xl transition-all duration-200"
            >
              Doar necesare
            </button>
            <button
              onClick={() => setShowDetails(!showDetails)}
              data-testid="cookie-show-details"
              className="w-full sm:w-auto px-6 py-3 text-gray-600 hover:text-[#0A192F] font-medium transition-colors"
            >
              {showDetails ? 'Ascunde detalii' : 'Detalii'}
            </button>
          </div>

          {/* Privacy link */}
          <p className="mt-4 text-xs text-gray-400 text-center sm:text-left">
            Aflați mai multe în{' '}
            <a href="/politica-confidentialitate" className="text-[#00BCD4] hover:underline">
              Politica de confidențialitate
            </a>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.5s ease-out forwards;
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default CookieConsent;
